import { BaseApiClient } from './base.client';

export interface AuthResponse {
  message: string;
  displayMessage?: string;
}

export interface MeResponse {
  message: string;
  data?: {
    email: string;
    _id: string;
    firstName?: string;
    lastName?: string;
    nameInitial?: string;
    fullName?: string;
  };
  statusCode?: number;
}

/** CMS get-contact-by-id 11-field projection — the My Profile page form model. */
export interface ContactInfo {
  salutation?: string;
  firstName?: string;
  lastName?: string;
  Email?: string;
  phone?: string;
  address1?: string;
  address2?: string;
  city?: string;
  countryCode?: string;
  country?: string;
  companyName?: string | null;
  vatNumber?: string | null;
}

export interface ContactResponse {
  message: string;
  data?: ContactInfo;
}

/** Whitelisted profile-update payload (session-scoped server-side; no userId). */
export interface PersonalInfoUpdate {
  salutation?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  address1?: string;
  address2?: string;
  city?: string;
  countryCode?: string;
  country?: string;
  companyName?: string | null;
  vatNumber?: string | null;
  travelWork?: string;
}

/** Change-password response — `errorCode === 3` signals an old-password mismatch. */
export interface ChangePasswordResponse {
  message: string;
  displayMessage?: string;
  errorCode?: number;
}

/** Full Member profile — used by the sports checkout page to auto-fill
 *  address / phone / city when the user is logged in. */
export interface MemberProfile {
  _id: string;
  Email: string;
  salutation?: string;
  firstName?: string;
  lastName?: string;
  address1?: string;
  address2?: string;
  city?: string;
  country?: string;
  countryCode?: string;
  phone?: string;
  companyName?: string | null;
  vatNumber?: string | null;
  travelWork?: string;
  Status?: string;
}

export interface ProfileResponse {
  message: string;
  data?: MemberProfile;
  statusCode?: number;
}

export class AuthClient extends BaseApiClient {
  constructor(baseUrl?: string) {
    // Env-driven base URL — no static fallback.
    //   • Server-side: prefer AUTH_SERVICE_URL (private LAN URL, no TLS).
    //   • Browser:     NEXT_PUBLIC_AUTH_API_URL (the public URL the
    //                  user's browser can reach).
    // BaseApiClient defers the missing-env error to first `request()`.
    const resolved =
      baseUrl ??
      (typeof window === 'undefined'
        ? (process.env.AUTH_SERVICE_URL ?? process.env.NEXT_PUBLIC_AUTH_API_URL)
        : process.env.NEXT_PUBLIC_AUTH_API_URL);
    super(resolved);
  }

  protected override async request<T>(
    endpoint: string,
    options?: RequestInit & { next?: { revalidate?: number | false; tags?: string[] } },
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const response = await fetch(url, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });

    return response.json() as Promise<T>;
  }

  async checkMemberExistence(email: string): Promise<AuthResponse> {
    return this.post<AuthResponse>('/checkmemberexistence', { Email: email });
  }

  async checkEmailVerification(email: string): Promise<AuthResponse> {
    return this.post<AuthResponse>('/checkemailverification', {
      Email: email,
      action: 'CheckEmailVerification',
    });
  }

  async signup(email: string, password: string): Promise<AuthResponse> {
    return this.post<AuthResponse>('/signup', { Email: email, Password: password });
  }

  async login(email: string, password: string): Promise<AuthResponse> {
    return this.post<AuthResponse>('/memberlogin', { Email: email, Password: password });
  }

  async forgotPassword(email: string): Promise<AuthResponse> {
    return this.post<AuthResponse>('/forgotpassword', { Email: email });
  }

  async updatePassword(resetId: string, password: string): Promise<AuthResponse> {
    return this.post<AuthResponse>('/updatepassword', { resetId, Password: password });
  }

  async verifyEmail(token: string): Promise<AuthResponse> {
    return this.get<AuthResponse>('/verifyemail', { token });
  }

  async getMe(): Promise<MeResponse> {
    return this.get<MeResponse>('/me');
  }

  async getProfile(): Promise<ProfileResponse> {
    return this.get<ProfileResponse>('/profile');
  }

  /** CMS get-contact-by-id projection for the My Profile form (session-scoped). */
  async getContact(): Promise<ContactResponse> {
    return this.get<ContactResponse>('/contact');
  }

  /** Update the logged-in member's profile (session-scoped server-side). */
  async updatePersonalInformation(payload: PersonalInfoUpdate): Promise<AuthResponse> {
    return this.post<AuthResponse>('/update-personal-information', payload);
  }

  /** Authenticated change-password (old + new). `errorCode === 3` ⇒ old password mismatch. */
  async changePassword(oldPassword: string, newPassword: string): Promise<ChangePasswordResponse> {
    return this.post<ChangePasswordResponse>('/change-password', {
      oldPassword,
      Password: newPassword,
    });
  }

  async logout(): Promise<AuthResponse> {
    return this.post<AuthResponse>('/logout', {});
  }
}

export const authClient = new AuthClient();
